import React, { useState, useRef } from "react";
import ReactCrop, { centerCrop, makeAspectCrop } from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import { useDropzone } from "react-dropzone";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { UploadCloud, X } from "lucide-react";
import { api } from "@/lib/api";

function centerAspectCrop(mediaWidth, mediaHeight, aspect) {
  return centerCrop(
    makeAspectCrop(
      { unit: "%", width: 90 },
      aspect,
      mediaWidth,
      mediaHeight
    ),
    mediaWidth,
    mediaHeight
  );
}

export default function ImageCropper({ value, onChange, aspect = 16 / 9, type = "cover", schoolId, width = 1024, height = 700 }) {
  const [upImg, setUpImg] = useState(null);
  const [crop, setCrop] = useState(null);
  const [completedCrop, setCompletedCrop] = useState(null);
  const imgRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);

  const { getRootProps, getInputProps } = useDropzone({
    accept: { "image/*": [".png", ".jpg", ".jpeg", ".webp"] },
    multiple: false,
    onDrop: (acceptedFiles) => {
      if (acceptedFiles && acceptedFiles.length > 0) {
        const reader = new FileReader();
        reader.addEventListener("load", () => setUpImg(reader.result));
        reader.readAsDataURL(acceptedFiles[0]);
      }
    },
  });

  const onImageLoad = (e) => {
    const { width, height } = e.currentTarget;
    setCrop(centerAspectCrop(width, height, aspect));
  };

  const uploadCropped = async () => {
    if (!completedCrop || !imgRef.current) return;
    setUploading(true);
    try {
      const canvas = document.createElement("canvas");
      const scaleX = imgRef.current.naturalWidth / imgRef.current.width;
      const scaleY = imgRef.current.naturalHeight / imgRef.current.height;
      canvas.width = completedCrop.width * scaleX;
      canvas.height = completedCrop.height * scaleY;
      const ctx = canvas.getContext("2d");

      ctx.drawImage(
        imgRef.current,
        completedCrop.x * scaleX,
        completedCrop.y * scaleY,
        completedCrop.width * scaleX,
        completedCrop.height * scaleY,
        0,
        0,
        completedCrop.width * scaleX,
        completedCrop.height * scaleY
      );

      canvas.toBlob(async (blob) => {
        if (!blob) {
          setUploading(false);
          return;
        }
        const fd = new FormData();
        fd.append("file", blob, `${type}.webp`);
        fd.append("media_type", type);
        const r = await api.post(`/api/v1/admin/schools/${schoolId}/upload-media`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setProgress(percentCompleted);
          },
        });
        if (r.data?.url) {
          onChange(r.data.url);
          setUpImg(null);
        }
        setUploading(false);
        setProgress(0);
      }, "image/webp", 0.9);
    } catch (err) {
      console.error(err);
      setUploading(false);
      setProgress(0);
    }
  };

  return (
    <div className="border border-border rounded-lg overflow-hidden bg-muted/20">
      {value ? (
        <div className="relative group">
          <img src={value} alt="Media" className="w-full h-auto max-h-40 object-cover" />
          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
            <Button variant="destructive" size="sm" onClick={() => onChange("")}><X className="h-4 w-4 mr-2"/> Kaldır</Button>
          </div>
        </div>
      ) : (
        <div {...getRootProps()} className="p-6 text-center cursor-pointer hover:bg-muted/40 transition-colors flex flex-col items-center gap-2">
          <input {...getInputProps()} />
          <UploadCloud className="h-8 w-8 text-muted-foreground" />
          <div className="text-sm font-medium">Sürükle bırak veya seç</div>
          <div className="text-xs text-muted-foreground">Kırpma işlemi için seçin (Önerilen: {width}x{height})</div>
        </div>
      )}

      <Dialog open={!!upImg} onOpenChange={(o) => !o && setUpImg(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Görseli Kırp</DialogTitle>
            <DialogDescription>İstediğiniz alanı seçerek kaydedin.</DialogDescription>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-auto flex justify-center bg-black/5">
            {upImg && (
              <ReactCrop
                crop={crop}
                onChange={(_, percentCrop) => setCrop(percentCrop)}
                onComplete={(c) => setCompletedCrop(c)}
                aspect={aspect}
              >
                <img ref={imgRef} src={upImg} alt="Crop me" onLoad={onImageLoad} />
              </ReactCrop>
            )}
          </div>
          <DialogFooter className="flex-col items-stretch gap-2 sm:flex-row sm:justify-end sm:items-center">
            {uploading && (
              <div className="flex-1 mr-4">
                <Progress value={progress} className="h-2 w-full" />
                <div className="text-xs text-right mt-1 text-muted-foreground">% {progress} {progress === 100 ? "Tamamlanıyor..." : "Yükleniyor"}</div>
              </div>
            )}
            <Button variant="outline" onClick={() => setUpImg(null)} disabled={uploading}>İptal</Button>
            <Button onClick={uploadCropped} disabled={uploading}>{uploading ? "Yükleniyor..." : "Kes ve Yükle"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
